import type { DatatableActionDefinition, DatatableRow } from '../../../types/datatable-types';

/**
 * Renders the action column cell for a row
 * @param row - Data row with Actions property
 * @param localization - Localization strings
 * @returns HTMLElement containing action buttons
 */
export function renderActionColumn<T extends DatatableRow>(row: T): HTMLElement {
  const container = document.createElement('div');
  container.className = 'datatable__actions';

  if (!row.Actions || row.Actions.length === 0) {
    return container;
  }

  // If only 1-2 actions total, show all inline
  if (row.Actions.length <= 3) {
    row.Actions.forEach((action) => {
      container.appendChild(renderActionButton(action, true));
    });
    return container;
  }

  // For 3+ actions, use responsive layout
  const screenWidth = window.innerWidth;
  let inlineCount = 0;

  if (screenWidth < 576) {
    // Mobile: dropdown only
    inlineCount = 0;
  } else if (screenWidth < 992) {
    // Tablet: 1 inline + dropdown
    inlineCount = 1;
  } else {
    // Desktop: 2 inline + dropdown
    inlineCount = 2;
  }

  // Render inline actions
  row.Actions.slice(0, inlineCount).forEach((action) => {
    container.appendChild(renderActionButton(action, true));
  });

  // Create dropdown for remaining actions
  const dropdown = document.createElement('div');
  dropdown.className = 'dropdown d-inline-block';
  dropdown.innerHTML = `
    <button class="btn btn-link dropdown-toggle datatable-actions-more-button" type="button" data-bs-toggle="dropdown" aria-expanded="false" data-bs-popper-config='{"strategy":"fixed"}'>
      <span class="iconify" data-icon="mdi:dots-horizontal" data-height="24" data-width="24"></span>
    </button>
    <ul class="dropdown-menu dropdown-menu-end datatable-dropdown-menu"></ul>
  `;

  const dropdownMenu = dropdown.querySelector('ul');
  if (dropdownMenu) {
    row.Actions.slice(inlineCount).forEach((action) => {
      const li = document.createElement('li');
      li.appendChild(renderActionButton(action, false));
      dropdownMenu.appendChild(li);
    });
  }

  container.appendChild(dropdown);

  return container;
}

/**
 * Renders a single action button
 * @param action - Action definition
 * @param inline - Whether button is inline (true) or in dropdown (false)
 * @returns HTMLElement (button or anchor)
 */
function renderActionButton(action: DatatableActionDefinition, inline: boolean): HTMLElement {
  const isGet = action.HttpMethod.toLowerCase() === 'get';
  const element = document.createElement(isGet ? 'a' : 'button');

  if (inline) {
    element.className = 'btn btn-link';
  } else {
    element.className = 'dropdown-item';
  }

  element.title = action.Name;

  // Create icon
  const icon = document.createElement('span');
  icon.className = 'iconify';
  icon.setAttribute('data-icon', action.IconName);
  icon.setAttribute('data-height', inline ? '24' : '20');
  icon.setAttribute('data-width', inline ? '24' : '20');
  element.appendChild(icon);

  // Add text for dropdown items
  if (!inline) {
    const text = document.createTextNode(` ${action.Name}`);
    element.appendChild(text);
  }

  // Handle different action types using discriminated union
  switch (action.ActionType) {
    case 0: // Modal
      element.classList.add('btn-modal');
      element.setAttribute('data-biz-action', action.Url);
      (element as HTMLAnchorElement).href = '#';
      break;

    case 1: // Command
      if (action.Confirmation.RequiresConfirmation) {
        element.classList.add('btn-confirm');
        element.setAttribute('data-biz-message', action.Confirmation.ConfirmationMessage || '');
        element.setAttribute('data-biz-action', action.Url);
        element.setAttribute('data-biz-method', action.HttpMethod);
      } else {
        // POST without confirmation
        element.setAttribute('type', 'button');
        element.addEventListener('click', async () => {
          try {
            const response = await fetch(action.Url, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
            });

            if (response.ok) {
              window.location.reload();
            } else {
              // eslint-disable-next-line no-console
              console.error('Action failed:', response.statusText);
            }
          } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Action error:', error);
          }
        });
      }
      break;

    case 2: // Link
      if (action.OpenInNewTab) {
        (element as HTMLAnchorElement).href = action.Url;
        element.setAttribute('target', '_blank');
        element.setAttribute('rel', 'noopener');
      } else {
        (element as HTMLAnchorElement).href = action.Url;
      }
      break;
  }

  return element;
}
